// src/widgets/ResponseEventWidget.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { RealtimeEvent } from '../utils/events.js';
import { useFabricService, MemoryIndexes } from '../contexts/FabricServiceContext';
import { useRealtimeService } from '../contexts/RealtimeServiceContext';
import { Toggle } from '../components/toggle/Toggle';
import { FilterDropdown } from '../components/FilterDropdown';
import { DropdownOption } from '../types/Dropdown';
import './ResponseEventWidget.scss';

interface ResponseEventWidgetProps {
  eventType: string;
  storageField: string;
  placeholder?: string;
  useResponseId?: boolean;
  itemContent: (eventItem: ResponseEventItem) => JSX.Element;
  speakerName: (eventItem: ResponseEventItem) => string | JSX.Element;
  filterCondition: (eventItem: ResponseEventItem, selectedModule: string, selectedFilters: (string | undefined)[]) => boolean;
  eventUpdate: (event: RealtimeEvent, output: ResponseEventUpdate, memoryIndexes?: MemoryIndexes[]) => void;
  processEvents?: () => ResponseEventUpdate;
  resetWidget?: (clearAll: () => void) => void;
}

export interface ResponseEventItem {
  responseId: string;
  id: string;
  role: string;
  type: string;
  output: any;
  data?: { timestamp: Date; source: string; selected?: boolean;[key: string]: any; };
}

export interface ResponseEventUpdate {
  modules: string[];
  filters: DropdownOption[];
  items: ResponseEventItem[];
  metadata?: { [key: string]: any }
}

export const ResponseEventWidget: React.FC<ResponseEventWidgetProps> = ({
  eventType,
  storageField,
  placeholder = 'awaiting thinking...',
  useResponseId = true,
  itemContent,
  speakerName,
  filterCondition,
  eventUpdate,
  processEvents: customProcessEvents,
  resetWidget: customResetWidget,
}) => {
  // Constants
  const ANIMATION_DELAY_PER_ITEM = 0.07; // seconds per item
  const MAX_TOTAL_ANIMATION_DURATION = 5000; // maximum 5 seconds for all animations
  const BASE_ANIMATION_DURATION = 600; // base animation duration from CSS (0.6s)

  // Context
  const { fabricEvents, memoryIndexes, useSystemTwo } = useFabricService();
  const { selectedItem, updateSelectedResponseId, selectedResponseId } = useRealtimeService();

  // State
  const [responseItems, setResponseItems] = useState<ResponseEventItem[]>([]);
  const [animatedItems, setAnimatedItems] = useState<Set<string>>(new Set());
  const [modules, setModules] = useState<string[]>([]);
  const [selectedModule, setSelectedModule] = useState<string>('');
  const [agentFilters, setAgentFilters] = useState<DropdownOption[]>([]);
  const [selectedAgentFilters, setSelectedAgentFilters] = useState<(string | undefined)[]>([]);

  // Process events and update state
  const defaultProcessEvents = useCallback(() => {
    const new_modules: string[] = [];
    const new_agents: DropdownOption[] = [];
    const new_items: ResponseEventItem[] = [];

    const update = {
      modules: new_modules,
      filters: new_agents,
      items: new_items,
      metadata: { "useSystemTwo": useSystemTwo }
    };

    fabricEvents
      .filter(
        (realtimeEvent) =>
          realtimeEvent.event.type === 'fabric.response.output_item.added' &&
          realtimeEvent.event.item.output[0].fabric_type === eventType
      )
      .forEach((realtimeEvent) => {
        eventUpdate(realtimeEvent, update, memoryIndexes);
      });

    return update;
  }, [fabricEvents, eventType, eventUpdate, memoryIndexes, useSystemTwo]);

  const defaultResetWidget = useCallback((clearAll: () => void) => {
    if (fabricEvents.length === 0) {
      clearAll()
    }
  }, [fabricEvents])

  const processEvents = customProcessEvents || defaultProcessEvents;
  const resetWidget = customResetWidget || defaultResetWidget;

  // Handle animation delays
  const getAnimationDelay = useCallback((item: ResponseEventItem, filteredItems: ResponseEventItem[]) => {
    if (animatedItems.has(item.id)) {
      return '0s';
    }

    const newItems = filteredItems.filter(i => !animatedItems.has(i.id));
    const newItemIndex = newItems.findIndex(i => i.id === item.id);

    const delay = Math.min(
      newItemIndex * ANIMATION_DELAY_PER_ITEM,
      MAX_TOTAL_ANIMATION_DURATION / 1000
    );

    return newItemIndex >= 0 ? `${delay}s` : '0s';
  }, [animatedItems]);

  // Main event processing effect
  useEffect(() => {
    const update = processEvents();

    setModules(prevModules => {
      const uniqueModules = Array.from(new Set(update.modules));
      return JSON.stringify(prevModules) === JSON.stringify(uniqueModules)
        ? prevModules
        : uniqueModules;
    });

    setAgentFilters(prevFilters => {
      const uniqueFilters = update.filters.filter(
        (option, index, self) =>
          index === self.findIndex((t) => t.value === option.value)
      );
      return JSON.stringify(prevFilters) === JSON.stringify(uniqueFilters)
        ? prevFilters
        : uniqueFilters;
    });

    setResponseItems(prevItems => {
      const newItems = update.items.filter(item =>
        !prevItems.some(prevItem => prevItem.id === item.id)
      );

      if (newItems.length > 0) {
        const totalDelay = (newItems.length - 1) * (ANIMATION_DELAY_PER_ITEM * 1000);
        const animationDuration = Math.min(
          totalDelay + BASE_ANIMATION_DURATION,
          MAX_TOTAL_ANIMATION_DURATION
        );

        setTimeout(() => {
          setAnimatedItems(prev => {
            const updated = new Set(prev);
            newItems.forEach(item => updated.add(item.id));
            return updated;
          });
        }, animationDuration);
      }

      return update.items.length === 0 ? prevItems : update.items;
    });

    resetWidget(() => {
      setModules([]);
      setAgentFilters([]);
      setResponseItems([]);
      setAnimatedItems(new Set());
    });

  }, [processEvents]);

  // Handle selected item updates
  useEffect(() => {
    if (useResponseId) {
      updateSelectedResponseId();
    }
  }, [selectedItem, updateSelectedResponseId]);

  // Load saved filters
  useEffect(() => {
    try {
      const savedAgentFilters = localStorage.getItem(`tmp::${storageField}`);
      if (savedAgentFilters) {
        const parsedFilters = JSON.parse(savedAgentFilters);
        if (JSON.stringify(parsedFilters) !== JSON.stringify(selectedAgentFilters)) {
          setSelectedAgentFilters(parsedFilters);
        }
      }
    } catch (error) {
      console.error('Error loading saved filters:', error);
    }
  }, [storageField]);

  // Handlers
  const handleChangeAgentFilters = useCallback((value: (string | undefined)[]) => {
    try {
      localStorage.setItem(`tmp::${storageField}`, JSON.stringify(value));
      setSelectedAgentFilters(value);
    } catch (error) {
      console.error('Error saving filters:', error);
    }
  }, [storageField]);

  const handleModuleChange = useCallback((index: number, value: string) => {
    setSelectedModule(value);
  }, []);

  // Render items
  const renderResponseItems = useCallback(() => {
    const filteredItems = responseItems.filter((item) => {
      if (!filterCondition(item, selectedModule, selectedAgentFilters)) {
        return false;
      }
      if (useResponseId && selectedResponseId && item.responseId !== selectedResponseId) {
        return false;
      }
      return true;
    });

    return filteredItems.map((eventItem) => (
      <div
        className={`conversation-item ${animatedItems.has(eventItem.id) ? 'animated' : ''}`}
        key={eventItem.id}
        style={{ '--animation-delay': getAnimationDelay(eventItem, filteredItems) } as React.CSSProperties}
      >
        <div className={`speaker ${eventItem.role || ''}`}>
          <div>{speakerName(eventItem)}</div>
        </div>
        <div className="speaker-content">
          {itemContent(eventItem)}
        </div>
      </div>
    ));
  }, [
    responseItems,
    selectedModule,
    selectedAgentFilters,
    selectedResponseId,
    animatedItems,
    getAnimationDelay,
    filterCondition,
    speakerName,
    itemContent
  ]);

  return (
    <div className="response-event-widget">
      <div className="content-main">
        <div className="content-logs">
          <div className="row">
            <div className="view-toggle-container">
              <Toggle
                defaultValue={selectedModule}
                labels={[eventType, ...modules.map(module => module.split('_')[0])]}
                values={['', ...modules]}
                onChange={handleModuleChange}
                buttonStyle="flush"
                spacing={8}
              />
            </div>
            <div className="filter-dropdown-wrapper">
              <FilterDropdown
                filterType={eventType}
                options={agentFilters}
                selectedFilters={selectedAgentFilters}
                onFilterChange={handleChangeAgentFilters}
              />
            </div>
          </div>
          <div className="content-view">
            <div className="content-block conversation">
              <div className="content-block-body">
                {!responseItems.length && placeholder}
                {renderResponseItems()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};